import * as React from 'react';

import { useAtomValue } from 'jotai';

import { $groupedCatalyticItems } from '../_state/loadForm.state';
import {
  CatalyticConverterSize,
  CatalyticConverterSizeOption,
  InventoryItemRecord,
} from '../types/load';
import {
  CATALYTIC_SIZE_OPTIONS,
  createGroupKey,
  getCatalyticConverterSize,
} from '../utils/catalyticConverter';

type UseCatalyticSizeOptionsResult = {
  currentSize: CatalyticConverterSize | undefined;
  availableSizeOptions: CatalyticConverterSizeOption[];
  findItemIdForSize: (size: CatalyticConverterSize) => string | undefined;
};

export const useCatalyticSizeOptions = (
  currentItem: InventoryItemRecord | undefined,
): UseCatalyticSizeOptionsResult => {
  const groupedItems = useAtomValue($groupedCatalyticItems);

  return React.useMemo(() => {
    if (!currentItem) {
      return {
        currentSize: undefined,
        availableSizeOptions: [],
        findItemIdForSize: () => undefined,
      };
    }

    const groupKey = createGroupKey(
      currentItem.itemGroup?.id,
      currentItem.name,
    );
    const group = groupedItems.groupsByKey.get(groupKey);

    if (!group) {
      return {
        currentSize: getCatalyticConverterSize(currentItem.metadata),
        availableSizeOptions: [],
        findItemIdForSize: () => undefined,
      };
    }

    const availableSizeOptions = CATALYTIC_SIZE_OPTIONS.filter((option) =>
      group.availableSizes.includes(option.key),
    );

    const findItemIdForSize = (
      size: CatalyticConverterSize,
    ): string | undefined => group.itemsBySize.get(size)?.id;

    return {
      currentSize: getCatalyticConverterSize(currentItem.metadata),
      availableSizeOptions,
      findItemIdForSize,
    };
  }, [currentItem, groupedItems]);
};
